import { useState, useEffect, useRef, useContext, useCallback } from 'react'
import axios from "axios";
import { getAuth } from "firebase/auth";
import toast from 'react-hot-toast';
import {
  Activity,
  ArrowLeft,
  Coins,
  CreditCard,
  FileText,
  History,
  Inbox,
  LayoutDashboard,
  Map,
  LogOut,
  ReceiptText,
  RefreshCcw,
  Server,
  ShieldCheck,
  Tags,
  UserCog,
  Users,
  Workflow,
} from 'lucide-react';

import ClassificationAdmin from './Admin/ClassificationAdmin.js';
import AuthDashboard from './Admin/AuthenticationAdmin.js';
import CreditsDashboard from './Admin/CreditsAdmin.js';
import SubscriptionsDashboard from './Admin/SubscriptionAdmin.js';
import CreditsDashboardHistory from './Admin/CreditsHistoryAdmin.js';
import TabsDashboard from './Admin/TabsDashboard.js';
import ExtractionsDashboard from './Admin/ExtractionsAdmin.js';
import DashboardHome from './Admin/DashboardHome.js';
import BillingDetails from './Admin/BillingDetails.js';
import QueueStatus from './Admin/QueueStatus.js';
import UserJobStats from './Admin/UserJobStats.js';
import InboundEmails from './Admin/InboundEmails.js';
import SafariRuns from './Admin/SafariRuns.js';
import WorkflowExtractionsAdmin from './Admin/WorkflowExtractionsAdmin.js';

import { formatDate } from '../utils/formatDates.js';
import { AuthContext } from "../utils/auth.js";
import { isAdminEmail } from '../utils/adminUsers.js';
import app from "../utils/firebaseApp.js";
import { Redirect, useHistory, useParams } from "react-router-dom";
import DevDarkModeToggle from '../components/DevDarkModeToggle.js';
import './Admin/admin.css';

const adminSections = [
  {
    name: 'Overview',
    shortName: 'Overview',
    description: 'Executive snapshot, user lookup, and fastest paths into the admin workflows.',
    group: 'Command',
    index: 0,
    slug: 'overview',
    icon: LayoutDashboard,
  },
  {
    name: 'Billing Details',
    shortName: 'Billing',
    description: 'Inspect per-user processing time, errors, request volume, and billing records.',
    group: 'Revenue',
    index: 1,
    slug: 'billing',
    icon: ReceiptText,
  },
  {
    name: 'Extractions',
    shortName: 'Extractions',
    description: 'Analyze extraction requests, pages, origins, internal users, and customer usage.',
    group: 'Usage',
    index: 2,
    slug: 'extractions',
    icon: FileText,
  },
  {
    name: 'Classifications',
    shortName: 'Classify',
    description: 'Track classification volume, pages, marketplace activity, and customer usage.',
    group: 'Usage',
    index: 3,
    slug: 'classifications',
    icon: Tags,
  },
  {
    name: 'Authentication',
    shortName: 'Users',
    description: 'Review accounts, signup velocity, providers, verification, and last-login data.',
    group: 'Users',
    index: 4,
    slug: 'authentication',
    icon: Users,
  },
  {
    name: 'Credits',
    shortName: 'Credits',
    description: 'Monitor credit purchases, usage, client totals, and page-based revenue.',
    group: 'Revenue',
    index: 5,
    slug: 'credits',
    icon: Coins,
  },
  {
    name: 'Subscriptions',
    shortName: 'Plans',
    description: 'Audit subscription plans, renewals, payments, and active subscription records.',
    group: 'Revenue',
    index: 6,
    slug: 'subscriptions',
    icon: CreditCard,
  },
  {
    name: 'Buy Button History',
    shortName: 'Buy History',
    description: 'Inspect Stripe button triggers and checkout history for purchase debugging.',
    group: 'Revenue',
    index: 7,
    slug: 'buy-history',
    icon: History,
  },
  {
    name: 'Queue Status',
    shortName: 'Queues',
    description: 'Watch worker queue load, active jobs, waiting jobs, delays, and failures.',
    group: 'Operations',
    index: 8,
    slug: 'queues',
    icon: Activity,
  },
  {
    name: 'User Job Stats',
    shortName: 'Jobs',
    description: 'Find users with stuck, waiting, failed, completed, and active worker jobs.',
    group: 'Operations',
    index: 9,
    slug: 'jobs',
    icon: UserCog,
  },
  {
    name: 'Inbound emails',
    shortName: 'Inbound',
    description: 'Inspect Mailgun inbound messages, bodies, and attachments. Read-only.',
    group: 'Operations',
    index: 10,
    slug: 'inbound-emails',
    icon: Inbox,
  },
  {
    name: 'Safari runs',
    shortName: 'Safari',
    description: 'Inbound Safari booking packs, validation reports, and SharePoint dry-run manifests.',
    group: 'Operations',
    index: 11,
    slug: 'safari',
    icon: Map,
  },
  {
    name: 'Workflow extractions',
    shortName: 'Workflows',
    description: 'Custom extractions that have a workflow id, across every user on the platform.',
    group: 'Usage',
    index: 12,
    slug: 'workflows',
    icon: Workflow,
  },
];

export default function Admin() {
  const { currentUser } = useContext(AuthContext);
  const canAccessAdmin = isAdminEmail(currentUser?.email);

  const serverUrl = process.env.REACT_APP_SERVER_URL;

  const history = useHistory();
  const { section } = useParams();
  const routeSection = adminSections.find((item) => item.slug === section);
  const viewNumber = routeSection ? routeSection.index : 0;

  const setViewNumber = useCallback((index) => {
    const target = adminSections.find((item) => item.index === index) || adminSections[0];
    history.push(target.index === 0 ? '/admin' : '/admin/' + target.slug);
  }, [history]);

  const [users, setUsers] = useState({});
  const [usersInfo, setUsersInfo] = useState({});

  const [authChart, setAuthChart] = useState([]);
  const [totalAuthRequests, setTotalAuthRequests] = useState(0);
  const [refreshingUsers, setRefreshingUsers] = useState(false);

  const handleSignout = () => {
    getAuth(app).signOut(app).then(() => {
      // console.log("Signout")
    }).catch(() => {
      // console.log(error)
    });
  }

  const getUsers = useCallback(async (useCache = true) => {
    if (!canAccessAdmin) return [];

    try {
      const idToken = await currentUser.getIdToken();
      const url = serverUrl + "/admin/getUsers?cache=" + useCache;

      const toastId = toast.loading('Fetching users...');
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${idToken}`
        }
      });

      if (!response.data) {
        toast.error('No users found', { id: toastId });
        return [];
      }

      toast.success('Users fetched successfully', { id: toastId });
      return response.data;
    } catch (error) {
      console.log(error);
      toast.error('Error fetching users');
      return [];
    }
  }, [canAccessAdmin, currentUser, serverUrl]);

  const processUsers = (users) => {
    const userIds = {};
    const usersInfo = {};

    users.forEach((user) => {
      userIds[user.userId] = user.email;
      usersInfo[user.userId] = { ...user };
    });

    setUsers(userIds);
    setUsersInfo(usersInfo);

    const totalAuthData = [];

    // transform each user's data into an array element
    Object.keys(usersInfo).forEach((userId) => {
      const userAuth = usersInfo[userId];
      const timestamp = userAuth.creationTime;
      const month = formatDate(new Date(timestamp));
      const newTimestamp = new Date(timestamp);

      // Check if an auth entry for this timestamp already exists
      const existingEntry = totalAuthData.find((entry) => entry.day === month);

      if (existingEntry) {
        existingEntry.Users += 1;
      } else {
        // Create a new entry for this timestamp
        const newEntry = {
          date: newTimestamp,
          day: month,
          Users: 1,
        };

        totalAuthData.push(newEntry);
      }
    });

    // sort the data by timestamp in ascending order
    totalAuthData.sort((a, b) => a.date - b.date);

    // calculate total Requests
    let totalRequests = 0;
    totalAuthData.forEach((entry) => {
      totalRequests += entry.Users;
    });
    setTotalAuthRequests(totalRequests);

    setAuthChart(totalAuthData);
  }

  const handleRefreshUsers = async () => {
    setRefreshingUsers(true);
    const usersData = await getUsers(false);
    processUsers(usersData);
    setRefreshingUsers(false);
  };

  const hasRun = useRef(false);

  useEffect(() => {
    if (!canAccessAdmin) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const appName = process.env.REACT_APP_NAME;
    document.title = "Admin - " + appName;

    const fetchData = async () => {
      const usersData = await getUsers();
      processUsers(usersData);
    }

    fetchData();
  }, [canAccessAdmin, getUsers]);

  const activeSection = adminSections.find((section) => section.index === viewNumber) || adminSections[0];
  const ActiveSectionIcon = activeSection.icon;
  const userCount = Object.keys(usersInfo).length || Object.keys(users).length;
  const latestSignup = Object.values(usersInfo)
    .map((user) => new Date(user.creationTime).getTime())
    .filter((timestamp) => !Number.isNaN(timestamp))
    .sort((a, b) => b - a)[0];

  const renderActiveView = () => {
    if (viewNumber === 0) {
      return (
        <DashboardHome
          users={users}
          usersInfo={usersInfo}
          authChart={authChart}
          totalAuthRequests={totalAuthRequests}
          sections={adminSections}
          setViewNumber={setViewNumber}
        />
      );
    }

    if (viewNumber === 1) return <BillingDetails users={users} />;
    if (viewNumber === 2) return <ExtractionsDashboard users={users} />;
    if (viewNumber === 3) return <ClassificationAdmin users={users} />;
    if (viewNumber === 4) {
      return (
        <AuthDashboard
          usersInfo={usersInfo}
          authChart={authChart}
          totalAuthRequests={totalAuthRequests}
        />
      );
    }
    if (viewNumber === 5) return <CreditsDashboard usersInfo={usersInfo} />;
    if (viewNumber === 6) return <SubscriptionsDashboard usersInfo={usersInfo} />;
    if (viewNumber === 7) return <CreditsDashboardHistory usersInfo={usersInfo} />;
    if (viewNumber === 8) return <QueueStatus />;
    if (viewNumber === 9) return <UserJobStats users={users} />;
    if (viewNumber === 10) return <InboundEmails />;
    if (viewNumber === 11) return <SafariRuns />;
    if (viewNumber === 12) return <WorkflowExtractionsAdmin users={users} />;

    return null;
  };

  if (!canAccessAdmin) {
    return <Redirect to="/dashboard" />;
  }

  if (section && !routeSection) {
    return <Redirect to="/admin" />;
  }

  return (
    <>
      <div className="extracta-admin">
        <main className="admin-page">
          <header className="admin-topbar">
            <a href="/dashboard" className="admin-brand-link" aria-label="Back to platform dashboard">
              <img
                src={process.env.PUBLIC_URL + '/logo_extracta_labs_white.png'}
                alt="Extracta LABS"
              />
            </a>
            <div className="admin-topbar-actions">
              <DevDarkModeToggle className="admin-secondary-button" />
              <a href="/dashboard" className="admin-secondary-button">
                <ArrowLeft className="h-4 w-4" />
                Back to platform
              </a>
              <a href="/admin/health" className="admin-secondary-button">
                <Server className="h-4 w-4" />
                Server health
              </a>
              <button type="button" onClick={handleSignout} className="admin-secondary-button">
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </header>

          <div className="admin-shell px-4 py-3 sm:px-6 lg:px-8">
            <div className="admin-hero">
              <div className="admin-hero-main">
                <div className="admin-kicker">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Extracta Admin
                </div>
                <h1>Operations cockpit</h1>
              </div>

              <div className="admin-hero-stats">
                <div>
                  <span>Total users</span>
                  <strong>{userCount.toLocaleString()}</strong>
                </div>
                <div>
                  <span>Latest signup</span>
                  <strong>{latestSignup ? new Date(latestSignup).toLocaleDateString() : '-'}</strong>
                </div>
              </div>

              <div className="admin-hero-actions">
                <button
                  onClick={handleRefreshUsers}
                  disabled={refreshingUsers}
                  className="admin-primary-button"
                >
                  {refreshingUsers ? (
                    <>
                      <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCcw className="h-3.5 w-3.5" />
                      Refresh Users
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="admin-content-grid">
              <TabsDashboard sections={adminSections} setViewNumber={setViewNumber} viewNumber={viewNumber} />

              <section className="admin-workspace" aria-label={activeSection.name}>
                <div className="admin-section-header">
                  <div>
                    <p>{activeSection.group}</p>
                    <h2>{activeSection.name}</h2>
                    <span>{activeSection.description}</span>
                  </div>
                  <div className="admin-section-icon">
                    <ActiveSectionIcon className="h-5 w-5" />
                  </div>
                </div>

                {renderActiveView()}
              </section>
            </div>
          </div >
        </main >
      </div >
    </>
  )
}
